import type { LocalMessage } from '@swchat/client-sdk';
import { useState } from 'react';
import { Badge, Icon, IconButton } from '../design';
import { formatBytes, isInlineImage } from '../lib/display';
import { useClient } from '../lib/hooks';
import { confirmSave } from '../lib/scan-download';
import { useDecryptedImage } from './FileContent';
import { downloadError, fileDateFmt, saveBlob } from './format';

/**
 * W2.3: tab "Tệp" — file/ảnh của hội thoại lấy từ store cục bộ (`listFiles`: gồm file gửi trong luồng, bỏ tin đã xóa),
 * mới nhất ở đầu. Không gọi API mới: tải về vẫn qua `downloadFile` (GET /files + giải mã trên máy). File đã hết quyền
 * (rời nhóm, file bị xóa, 403/404) hiện "Không còn quyền tải".
 */
export function FilesTab({
  files,
  me,
  noAccess,
  senderName,
  onOpenThread,
}: {
  files: LocalMessage[];
  me?: string;
  /** Đã rời hội thoại → server không cho tải nữa (như C3 ở dòng chính). */
  noAccess: boolean;
  senderName: (userId: string) => string | undefined;
  onOpenThread: (rootId: string) => void;
}) {
  if (!files.length) {
    return (
      <div className="ws-tab-empty" data-testid="files-list">
        <Icon name="file" size={22} />
        <p>Chưa có tệp nào trong cuộc trò chuyện này.</p>
      </div>
    );
  }
  return (
    <ul className="ws-tab-list" data-testid="files-list" aria-label="Tệp">
      {files.map((m) => (
        <FileItem
          key={m.id}
          m={m}
          sender={m.sender_user_id === me ? 'Bạn' : (senderName(m.sender_user_id) ?? '…')}
          noAccess={noAccess}
          onOpenThread={onOpenThread}
        />
      ))}
    </ul>
  );
}

function FileItem({
  m,
  sender,
  noAccess,
  onOpenThread,
}: {
  m: LocalMessage;
  sender: string;
  noAccess: boolean;
  onOpenThread: (rootId: string) => void;
}) {
  const client = useClient();
  const file = m.file!;
  const image = isInlineImage(file);
  // Ảnh xem trước: cùng cơ chế giải mã với bong bóng tin (object URL thu hồi khi rời tab / đổi hội thoại).
  const [preview] = useDecryptedImage(m, image && !noAccess);
  const [error, setError] = useState<string | undefined>(noAccess ? 'Không còn quyền tải' : undefined);
  const [progress, setProgress] = useState<number>();

  async function download() {
    setError(undefined);
    setProgress(0);
    try {
      const bytes = await client.downloadFile(m, { onProgress: setProgress });
      if (!(await confirmSave(client, file.name, bytes))) return;
      saveBlob(new Blob([bytes as BlobPart], { type: file.mime || 'application/octet-stream' }), file.name);
    } catch (e) {
      setError(downloadError(e));
    } finally {
      setProgress(undefined);
    }
  }

  return (
    <li className="ws-files-item" data-testid="file-item" data-file-name={file.name} data-message-id={m.id}>
      <span className="ws-files-thumb" aria-hidden={!preview}>
        {preview ? <img data-testid="file-item-preview" src={preview} alt={`Xem trước ${file.name}`} /> : <Icon name="file" size={20} />}
      </span>
      <span className="ws-files-body">
        <span className="ws-files-name" data-testid="file-item-name" title={file.name}>
          {file.name}
        </span>
        <span className="ws-small ws-muted ws-files-meta">
          <span data-testid="file-item-sender">{sender}</span>
          {' · '}
          <time data-testid="file-item-date" dateTime={new Date(m.ts).toISOString()}>
            {fileDateFmt.format(m.ts)}
          </time>
          {' · '}
          <span data-testid="file-item-size">{formatBytes(file.size)}</span>
          {m.thread_root && (
            <>
              {' '}
              <button type="button" className="ws-files-thread" data-testid="file-item-thread" onClick={() => onOpenThread(m.thread_root!)}>
                <Badge tone="neutral">trong luồng</Badge>
              </button>
            </>
          )}
        </span>
        {(error || progress !== undefined) && (
          <span className="ws-small ws-muted" data-testid={error === 'Không còn quyền tải' ? 'file-no-access' : 'file-item-status'} role="status">
            {progress !== undefined ? `Đang tải… ${Math.round(progress * 100)}%` : error}
          </span>
        )}
      </span>
      {!noAccess && (
        <IconButton
          label={`Tải xuống ${file.name}`}
          icon="download"
          size={18}
          className="ws-file-dl"
          data-testid="file-item-download"
          disabled={progress !== undefined}
          onClick={() => void download()}
        />
      )}
    </li>
  );
}
